import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import axios from 'axios';
import { 
    MenuItem, 
    Select, 
    FormControl, 
    InputLabel, 
    TextField, 
    InputAdornment, 
    TablePagination
} from '@material-ui/core';
import _ from 'lodash';
import { NoDataPlaceholder } from '../components/Placeholders/NoDataPlaceholder';
import { Placeholders } from '../components/Placeholders';
import { useAuth } from '../context/auth';
import { CampaignType } from './Campaign/Campaign.interface';
import { PaginationWrapper } from '../Payments/Payments.styles';
import {
    InputWrapper,
    StyledSearchIcon,
    InfoRow,
    MobileWrapper
} from './Campaigns.styles';
import { Campaign } from './Campaign';
import { CampaignTypeApiResponse, CampaignsParamsProps } from './Campaigns.interface';
import { allStatuses, allTypes } from './utilities';

export function Campaigns() {
    const { status } = useParams<CampaignsParamsProps>();
    const [adminAccess, setAdminAccess] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState('');
    const [searchStatus, setSearchStatus] = useState(status ? status : '');
    const [searchType, setSearchType] = useState('');
    const [searchOwner, setSearchOwner] = useState('');
    const [campaignsData, setCampaignsData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalResults, setTotalResults] = useState(0);
    const { user, authToken } = useAuth();
    const [isSearching, setIsSearching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const handleChangeStatus = (event: React.ChangeEvent<{ value: unknown }>) => {
        setSearchStatus(event.target.value as string);
    }
    const handleChangeType = (event: React.ChangeEvent<{ value: unknown }>) => {
        setSearchType(event.target.value as string);
    }
    const delayedQuery = useCallback(_.debounce((title, owner, status, type, page, rows) => {
        fetchData(title, owner, status, type, page, rows);
    }, 500), []);
    const fetchData = (title?: string, owner?: string, status?: string, type?: string, page?: number, rows?: number) => {
        let url = `/api/campaign/campaign/`;
        setIsLoading(true);
        if (title){
            url += `${url.includes('?') ? '&' : '?'}title=${encodeURIComponent(title)}`
        }
        if (owner) {
            url += `${url.includes('?') ? '&' : '?'}owner=${encodeURIComponent(owner)}`
        }
        if (status) {
            url += `${url.includes('?') ? '&' : '?'}status=${status}`
        }
        if (type) {
            url += `${url.includes('?') ? '&' : '?'}campaign_type=${type}`
        }
        if (page && page !== 0) {
            url += `${url.includes('?') ? '&' : '?'}page=${page + 1}`
        }
        if (rows && rows !== 10) {
            url += `${url.includes('?') ? '&' : '?'}page_size=${rows}`
        }
        if (title || owner || status || type) {
            setIsSearching(true);
        }
        else {
            setIsSearching(false);
        }
        axios.get(`${process.env.REACT_APP_BASE_URL}${url}`, {headers: { DTOKEN: `${authToken}`}}).then(res => {
            const results = res.data.results.map((campaign: CampaignTypeApiResponse) => {
                return {
                    ...campaign,
                    adType: campaign.ad_type,
                    startDate: campaign.start_date,
                    endDate: campaign.end_date,
                    dovepointsLimit: campaign.dovepoints_limit,
                    dovepointsUsed: campaign.dovepoints_used
                }
            })
            setTotalResults(res.data.count);
            setCampaignsData(results);
            setIsLoading(false);
        })
    }
    useEffect(() => {
        setPage(0);
    }, [searchOwner, searchStatus, searchType, searchValue])
    useEffect(() => {
        window.scrollTo(0, 0);
        delayedQuery(searchValue, searchOwner, searchStatus, searchType, page, rowsPerPage);
    }, [searchOwner, searchStatus, searchType, searchValue, rowsPerPage, page])
    useEffect(() => {
        if (user) {
            setAdminAccess(user.isAdmin);
        }
    }, [user])
    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    return (
        <>
            <InputWrapper adminAccess={adminAccess}>
                <TextField
                    variant="outlined" 
                    placeholder="Search"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <StyledSearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                {adminAccess &&
                    <TextField
                        variant="outlined" 
                        placeholder="Owner"
                        value={searchOwner}
                        onChange={(event) => setSearchOwner(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <StyledSearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                }
                <FormControl variant="outlined">
                    <InputLabel htmlFor="statusSelect">Status</InputLabel>
                    <Select
                        value={searchStatus}
                        onChange={handleChangeStatus}
                        label="Stauts"
                        inputProps={{
                            name: 'status',
                            id: 'statusSelect',
                        }}
                    >
                        {allStatuses.map((el) => (
                            <MenuItem key={el.label} value={el.value}>{el.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="outlined">
                    <InputLabel htmlFor="typeSelect">Type</InputLabel>
                    <Select
                        value={searchType}
                        onChange={handleChangeType}
                        label="Type"
                        inputProps={{
                            name: 'type',
                            id: 'typeSelect',
                        }}
                    >
                        {allTypes.map((el) => (
                            <MenuItem key={el.label} value={el.value}>{el.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </InputWrapper>
            {isLoading ? <Placeholders />
            :
            <>
                {campaignsData.length > 0
                    ?
                        <>
                            {isMobile
                                ?
                                    <MobileWrapper>
                                        {campaignsData.length > 0 && campaignsData.map((campaign: CampaignType) => (
                                            <Campaign key={campaign.id} data={campaign} adminAccess={adminAccess} refresh={fetchData} />
                                        ))}
                                    </MobileWrapper>
                                :
                                <>
                                    <InfoRow adminAccess={adminAccess}>
                                        <p>Campaign Title</p>
                                        {adminAccess && <p>Owner</p>}
                                        <p>Status</p>
                                        <p>Type</p>
                                        <p>Start Date</p>
                                        <p>DovePoints Used</p>
                                    </InfoRow>
                                    {campaignsData.map((campaign: CampaignType) => (
                                        <Campaign key={campaign.id} data={campaign} adminAccess={adminAccess} refresh={fetchData} />
                                    ))}
                                </>
                            }
                            <PaginationWrapper>
                                <TablePagination
                                    component="div"
                                    count={totalResults}
                                    page={page}
                                    onChangePage={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onChangeRowsPerPage={handleChangeRowsPerPage}
                                />
                            </PaginationWrapper>
                        </>
                    : <NoDataPlaceholder pageName='Campaigns' search={isSearching} />
                }
            </>
            
            }
        </>
    )
}
